import { EMPTY, from } from 'rxjs';
import { ofType, combineEpics } from 'redux-observable';
import { switchMap, map, expand, reduce } from 'rxjs/operators';

import { rs } from 'core/api';
import { actions } from '../actions';
import { errorMap } from 'core/redux/actions';
import { internalizePage, timestamps } from 'core/redux/serializers';
import { exportTepHistoryReport } from 'helpers/export/exportTepHistoryReport';

const internalize = timestamps(['createdAt', 'seenAt', 'sinceAt']).internalize;

const loadEntryPassesEpic = (action$) => {
	return action$.pipe(
		ofType(actions.tepHistory.loadEntryPasses.type),
		switchMap((action) => {
			const pageSize = 100;
			const initialParams = { no: 1, size: pageSize };

			const loadPage = (params) =>
				rs('veta/entry-passes/page')
					.optional('no', params.no)
					.param('size', params.size)
					.defer$()
					.pipe(map(internalizePage(params.size, internalize)));

			return loadPage(initialParams).pipe(
				expand((page) => {
					if (page.no >= Math.ceil(page.total / pageSize)) {
						return EMPTY;
					}
					return loadPage({ ...initialParams, no: page.no + 1 });
				}),
				reduce((acc, page) => {
					return [...acc, ...page.objects];
				}, []),
				map((entryPasses) => actions.tepHistory.setEntryPasses({ entryPasses })),
				errorMap(actions.tepHistory.fail),
			);
		}),
	);
};

const loadCompaniesEpic = (action$) => {
	return action$.pipe(
		ofType(actions.tepHistory.loadCompanies.type),
		switchMap((action) =>
			rs('veta/entry-passes/companies')
				.defer$()
				.pipe(
					map((companies) => actions.tepHistory.setCompanies({ companies })),
					errorMap(actions.tepHistory.fail),
				),
		),
	);
};

const loadHistoryEpic = (action$) => {
	return action$.pipe(
		ofType(actions.tepHistory.loadHistory.type),
		switchMap((action) =>
			rs(`veta/reports/leases/history?${action.filters}`)
				.optional('no', action.no)
				.param('size', action.size)
				.defer$()
				.pipe(
					map((history) => actions.tepHistory.setHistory({ history })),
					errorMap(actions.tepHistory.fail),
				),
		),
	);
};

const exportEpic = (action$, state$) => {
	return action$.pipe(
		ofType(actions.tepHistory.export.request.type),
		switchMap((action) => {
			const pageSize = 100;
			const filters = state$.value.reports.tepHistory.filters;
			const initialParams = { no: 1, size: pageSize, filters };

			const loadPage = (params) =>
				rs(`veta/reports/leases/history?${params.filters}`)
					.optional('no', params.no)
					.param('size', params.size)
					.defer$()
					.pipe(map(internalizePage(params.size, internalize)));

			return loadPage(initialParams).pipe(
				expand((page) => {
					if (page.no >= Math.ceil(page.total / pageSize)) {
						return EMPTY;
					}
					return loadPage({ ...initialParams, no: page.no + 1 });
				}),
				reduce((acc, page) => {
					return [...acc, ...page.objects];
				}, []),
				switchMap((allData) => {
					const reportSummary = state$.value.reports.tepHistory;
					return from(exportTepHistoryReport(action.format, allData, reportSummary)).pipe(
						map(() => actions.tepHistory.export.success()),
					);
				}),
				errorMap(actions.tepHistory.export.fail),
			);
		}),
	);
};

const epic = combineEpics(loadEntryPassesEpic, loadCompaniesEpic, loadHistoryEpic, exportEpic);

export { epic };
